window.VAE = window.VAE || {};

VAE.StateManager = (function () {
  'use strict';

  const STORAGE_KEY = 'vae_accessibility_state';

  const DEFAULT_STATE = {
    profile: null,
    text: {
      fontSize: 1,
      lineHeight: 1,
      letterSpacing: 0,
      wordSpacing: 0,
      fontFamily: 'default',
      textAlign: 'left',
      highlightLinks: false,
      highlightHeadings: false,
      dyslexiaFont: false,
      readabilityMode: false,
      readingGuide: false,
    },
    colors: {
      contrast: 'normal',
      colorScheme: 'default',
      saturation: 1,
      backgroundColor: '#ffffff',
      textColor: '#000000',
      linkColor: '#0000ee',
      highlightColor: '#ffff00',
      darkMode: false,
      highContrast: false,
      highContrastLight: false,
      grayscale: false,
      invertColors: false,
      colorBlindMode: 'none',
      highlightInteractive: false,
      accessibleCursor: false,
    },
    motion: {
      reducedMotion: false,
      animationsDisabled: false,
      animationSpeed: 1,
      autoplayVideos: true,
      smoothScrolling: true,
      cursorSize: 1,
      cursorContrast: false,
      focusIndicator: false,
    },
    language: {
      // todo
    },
  };

  const subscribers = {
    text: [],
    colors: [],
    motion: [],
    profile: [],
    language: [],
    global: [],
  };

  let initialized = false;
  let state = JSON.parse(JSON.stringify(DEFAULT_STATE));

  function isLocalStorageAvailable() {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn('VAE.StateManager: localStorage is not available:', e);
      return false;
    }
  }

  function saveStateToStorage() {
    if (!isLocalStorageAvailable()) return;

    try {
      const serializedState = JSON.stringify(state);
      localStorage.setItem(STORAGE_KEY, serializedState);
      console.log('VAE.StateManager: State saved to localStorage', {
        key: STORAGE_KEY,
        stateSize: serializedState.length,
      });
    } catch (e) {
      console.error(
        'VAE.StateManager: Failed to save state to localStorage:',
        e
      );
    }
  }

  function loadStateFromStorage() {
    if (!isLocalStorageAvailable()) return null;

    try {
      const serializedState = localStorage.getItem(STORAGE_KEY);
      if (serializedState) {
        console.log('VAE.StateManager: Found saved state in localStorage', {
          key: STORAGE_KEY,
          stateSize: serializedState.length,
        });
        return JSON.parse(serializedState);
      } else {
        console.log('VAE.StateManager: No saved state found in localStorage');
      }
    } catch (e) {
      console.error(
        'VAE.StateManager: Failed to load state from localStorage:',
        e
      );
    }
    return null;
  }

  const savedState = loadStateFromStorage();
  if (savedState) {
    console.log('VAE.StateManager: Preloading state from localStorage');
    if (savedState.text) state.text = { ...state.text, ...savedState.text };
    if (savedState.colors)
      state.colors = { ...state.colors, ...savedState.colors };
    if (savedState.motion)
      state.motion = { ...state.motion, ...savedState.motion };
    if (savedState.profile) state.profile = savedState.profile;
    if (savedState.language)
      state.language = { ...state.language, ...savedState.language };
    console.log('VAE.StateManager: Preloaded state:', state);
  }

  function init() {
    if (initialized) {
      console.log('VAE.StateManager: Already initialized, skipping');
      return state;
    }

    console.log('VAE.StateManager: Initializing state manager');

    let stateUpdated = false;

    try {
      const textState = VAE.Text.getCurrentState();
      if (textState && Object.keys(textState).length > 0) {
        const textStateToApply = {};
        Object.keys(textState).forEach((key) => {
          if (state.text[key] === undefined) {
            textStateToApply[key] = textState[key];
          }
        });

        if (Object.keys(textStateToApply).length > 0) {
          state.text = { ...state.text, ...textStateToApply };
          stateUpdated = true;
          console.log(
            'VAE.StateManager: Updated text state with module values:',
            textStateToApply
          );
        }
      }
    } catch (e) {
      console.error(
        'VAE.StateManager: Error getting text state from module',
        e
      );
    }

    try {
      const colorsState = VAE.Colors.getCurrentState();
      if (colorsState && Object.keys(colorsState).length > 0) {
        const colorsStateToApply = {};
        Object.keys(colorsState).forEach((key) => {
          if (state.colors[key] === undefined) {
            colorsStateToApply[key] = colorsState[key];
          }
        });

        if (Object.keys(colorsStateToApply).length > 0) {
          state.colors = { ...state.colors, ...colorsStateToApply };
          stateUpdated = true;
          console.log(
            'VAE.StateManager: Updated colors state with module values:',
            colorsStateToApply
          );
        }
      }
    } catch (e) {
      console.error(
        'VAE.StateManager: Error getting colors state from module',
        e
      );
    }

    try {
      const motionState = VAE.Motion.getCurrentState();
      if (motionState && Object.keys(motionState).length > 0) {
        const motionStateToApply = {};
        Object.keys(motionState).forEach((key) => {
          if (state.motion[key] === undefined) {
            motionStateToApply[key] = motionState[key];
          }
        });

        if (Object.keys(motionStateToApply).length > 0) {
          state.motion = { ...state.motion, ...motionStateToApply };
          stateUpdated = true;
          console.log(
            'VAE.StateManager: Updated motion state with module values:',
            motionStateToApply
          );
        }
      }
    } catch (e) {
      console.error(
        'VAE.StateManager: Error getting motion state from module',
        e
      );
    }

    if (stateUpdated) {
      console.log(
        'VAE.StateManager: State updated with module values, saving to localStorage'
      );
      saveStateToStorage();
    }

    initialized = true;
    console.log('VAE.StateManager: Initialization complete with state:', state);
    
    notifyAllSubscribers();

    return state;
  }

  function getState(section) {
    if (section === 'profile') {
      return state.profile;
    }
    if (section && state[section]) {
      return { ...state[section] };
    }
    return { ...state };
  }

  function updateState(section, newValues) {
    if (!state[section]) {
      console.error(`VAE.StateManager: Unknown section "${section}"`);
      return null;
    }

    console.log(`VAE.StateManager: Updating ${section} state with:`, newValues);

    const previousState = { ...state[section] };
    state[section] = { ...state[section], ...newValues };

    let hasChanges = false;
    const changedKeys = [];

    Object.keys(newValues).forEach((key) => {
      if (
        JSON.stringify(previousState[key]) !==
        JSON.stringify(state[section][key])
      ) {
        hasChanges = true;
        changedKeys.push(key);
      }
    });

    if (!hasChanges) {
      console.log(
        `VAE.StateManager: No actual changes in ${section} state, skipping update`
      );
      return { ...state[section] };
    }

    console.log(`VAE.StateManager: ${section} state updated:`, {
      previous: previousState,
      current: state[section],
      changed: changedKeys,
    });

    saveStateToStorage();
    notifySubscribers(section);
    notifySubscribers('global');

    return { ...state[section] };
  }

  function setActiveProfile(profileKey) {
    console.log(`VAE.StateManager: Setting active profile to: ${profileKey}`);

    if (state.profile === profileKey) {
      console.log(
        'VAE.StateManager: Profile already set to this value, skipping update'
      );
      return profileKey;
    }

    state.profile = profileKey;
    saveStateToStorage();
    notifySubscribers('profile');
    notifySubscribers('global');

    return profileKey;
  }

  function subscribe(section, callback) {
    if (typeof callback !== 'function') {
      console.error('VAE.StateManager: Subscriber callback must be a function');
      return false;
    }

    if (section === '*') {
      subscribers.global.push(callback);
      console.log(
        `VAE.StateManager: Added global subscriber, total: ${subscribers.global.length}`
      );
      
      callback(getState());
      return true;
    } else if (subscribers[section]) {
      subscribers[section].push(callback);
      console.log(
        `VAE.StateManager: Added ${section} subscriber, total: ${subscribers[section].length}`
      );
      
      callback(getState(section));
      return true;
    }

    console.error(`VAE.StateManager: Unknown section "${section}"`);
    return false;
  }

  function notifySubscribers(section) {
    console.log(
      `VAE.StateManager: Notifying subscribers for section: ${section}`
    );

    if (section === 'global') {
      if (subscribers.global.length > 0) {
        console.log(
          `VAE.StateManager: Calling ${subscribers.global.length} global subscribers`
        );
        const fullState = getState();
        subscribers.global.forEach((callback) => {
          try {
            callback(fullState);
          } catch (e) {
            console.error('VAE.StateManager: Error in global subscriber', e);
          }
        });
      }
    } else if (subscribers[section] && subscribers[section].length > 0) {
      console.log(
        `VAE.StateManager: Calling ${subscribers[section].length} ${section} subscribers`
      );
      const sectionState = getState(section);
      subscribers[section].forEach((callback) => {
        try {
          callback(sectionState);
        } catch (e) {
          console.error(`VAE.StateManager: Error in ${section} subscriber`, e);
        }
      });
    } else {
      console.log(`VAE.StateManager: No subscribers for ${section}`);
    }
  }

  function notifyAllSubscribers() {
    console.log('VAE.StateManager: Notifying all subscribers with current state');
    
    Object.keys(subscribers).forEach(section => {
      if (section !== 'global') {
        notifySubscribers(section);
      }
    });
    
    notifySubscribers('global');
  }

  function resetAll() {
    console.log('VAE.StateManager: Resetting all state to defaults');

    state = JSON.parse(JSON.stringify(DEFAULT_STATE));

    saveStateToStorage();
    notifyAllSubscribers();

    return { ...state };
  }

  return {
    init,
    getState,
    updateState,
    setActiveProfile,
    subscribe,
    resetAll,
    notifySubscribers,
    notifyAllSubscribers,
  };
})();